import { CursorDirectionEnum } from '@/types/generated/graphql';
import { ApolloError, DocumentNode, OperationVariables, QueryHookOptions, useQuery } from '@apollo/client';
import { useIntersectionObserver } from '@shared/hooks/use-intersection-observer';
import { useEffect, useState } from 'react';

type Props<QueryType, VariablesType extends OperationVariables> = {
  schema: DocumentNode;
  variables?: VariablesType;
  responseDataKey: keyof Omit<QueryType, '__typename'>;
  options?: QueryHookOptions;
};

export type UseCursorPaginationReturn<T> = {
  loading: boolean;
  loadNextRef: (node?: Element | null | undefined) => void;
  hasNextPage: boolean;
  initialLoading: boolean;
  hasPreviousPage: boolean;
  data: T;
  error: ApolloError | undefined;
  refetch: (variables?: Partial<OperationVariables> | undefined) => void;
};

const defaultVariables = {
  cursor: null,
  direction: null,
  limit: 15,
  sort: null,
  query: '',
};

export function useCursorPagination<
  QueryType,
  VariablesType extends OperationVariables,
  K extends keyof Omit<QueryType, '__typename'> = keyof Omit<QueryType, '__typename'>,
>(props: Props<QueryType, VariablesType>): UseCursorPaginationReturn<QueryType[K]> {
  const { schema, variables = defaultVariables, responseDataKey, options } = props;
  const { ref: loadNextRef, isIntersecting: isNextIntersecting } = useIntersectionObserver();
  const [cursorInfo, setCursorInfo] = useState({
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const { data, loading, error, fetchMore, refetch } = useQuery(schema, {
    ...options,
    variables: {
      ...defaultVariables,
      ...variables,
    },
    //   // !! REMOVE THIS CODE LATER, CAUSES
    // onCompleted: (data) => {
    //   const {
    //     startCursor = '',
    //     endCursor = '',
    //     hasNextPage = false,
    //     hasPreviousPage = false,
    //   } = data?.[responseDataKey]?.cursor ?? {};

    //   setCursorInfo({
    //     startCursor,
    //     endCursor,
    //     hasNextPage,
    //     hasPreviousPage,
    //   });
    // },
  });

  async function refetchHandler(variables?: Partial<OperationVariables>) {
    const res = await refetch(variables);

    const {
      startCursor = '',
      endCursor = '',
      hasNextPage = false,
      hasPreviousPage = false,
    } = res?.data?.[responseDataKey]?.cursor ?? {};

    setCursorInfo({
      startCursor,
      endCursor,
      hasNextPage,
      hasPreviousPage,
    });
  }

  function fetchNextPage() {
    fetchMore({
      variables: {
        ...variables,
        cursor: cursorInfo.endCursor,
        direction: CursorDirectionEnum.next,
      },
    });
  }

  useEffect(
    function onNextIntersection() {
      const shouldFetchNext = cursorInfo.hasNextPage && isNextIntersecting;
      if (shouldFetchNext) fetchNextPage();
    },

    [isNextIntersecting, cursorInfo]
  );

  useEffect(
    function setNewCursorIndex() {
      const {
        startCursor = '',
        endCursor = '',
        hasNextPage = false,
        hasPreviousPage = false,
      } = data?.[responseDataKey]?.cursor ?? {};

      setCursorInfo({
        startCursor,
        endCursor,
        hasNextPage,
        hasPreviousPage,
      });
    },
    [data]
  );

  return {
    refetch: refetchHandler,
    loading,
    error,
    loadNextRef,
    hasNextPage: cursorInfo.hasNextPage,
    initialLoading: !data?.data && loading,
    hasPreviousPage: cursorInfo.hasPreviousPage,
    data: data?.[responseDataKey],
  };
}
